import React, { useRef, useEffect, useState } from "react";
import { intervalSelectorGraphAdapter } from "./interval-selector-graph-adapter";
import getSubfragm from "../../../../../graphics/get-subfragm";
import { prepareSelectorHOC } from "../../../../../hoc/prepareSelectorHOC";

import { ErrorIndicator, Spinner } from "../../../../state-indicators";

import VisualizatorSettings from "./VisualizatorSettings.json";

const graphWrapStyle = {
    height: "100%",
    width: "100%",
    backgroundColor: "white"
};

const IntervalSelectorGraph = props => {
    const graphAdapter = useRef(null);
    const canvasWrapRef = useRef(null);
    const graphWidth = useRef(null);
    const freshDataProps = useRef(null);
    const computedComplexes = useRef(null);
    const signalStart = useRef(null);
    const isMounted = useRef(false);
    const signalDataRef = useRef(null);
    const channelRef = useRef(null);
    const [signalData, setSignalData] = useState(null);
    const [state, setState] = useState("loading"); // "loading", "error", "idle"
    const { channelNames, fd, channel, userSweepUnits } = props;

    useEffect(() => {
        const mount = async () => {
            graphAdapter.current = new intervalSelectorGraphAdapter({
                canvasRef: canvasWrapRef.current,
                visualSettings: VisualizatorSettings,
                fd: fd,
                cursorCallback: onCursorMove
            });

            graphAdapter.current.changeDefaultFrequencySettungs(fd);
            await graphAdapter.current.initializeGraph();
            await graphAdapter.current.setupCursors();
            await graphAdapter.current.setupLabel();
            graphAdapter.current.setUserSweepUnits(userSweepUnits);

            graphWidth.current = graphAdapter.current.getWidthInPoints(fd);
            prepareSignalStart();
            prepareCursorsToInitialLocation();
            prepareSignalData();

            isMounted.current = true;

            graphAdapter.current.onResize(update);
        };

        mount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const update = () => {
        const signalData = signalDataRef.current;
        const channel = channelRef.current;
        if (signalData) {
            graphAdapter.current.plotData(signalData[channel]);
            graphAdapter.current.setLabelText(channelNames[channel]);
        }
    };

    useEffect(() => {
        signalDataRef.current = signalData;
        channelRef.current = channel;
        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalData, channel]);

    useEffect(() => {
        freshDataProps.current = props.data;
    }, [props.data]);

    useEffect(() => {
        if (isMounted.current) {
            graphAdapter.current.setUserSweepUnits(userSweepUnits);
        }
    }, [userSweepUnits]);

    const prepareSignalStart = () => {
        const { signalLength, changeSettings, desiredCenter_pnt } = props;
        let { Q, S } = props.data;
        Q = convertMsToPoints(Q);
        S = convertMsToPoints(S);
        let desiredCenter = desiredCenter_pnt;

        if (desiredCenter === null) {
            desiredCenter = Q + S / 2;
            changeSettings({ desiredCenter_pnt: desiredCenter });
        }

        const { start } = getSubfragm(signalLength, Math.round(desiredCenter), graphWidth.current);
        signalStart.current = start;
    };

    const prepareSignalData = async () => {
        await props
            .reqSignal(signalStart.current, graphWidth.current)
            .then(_data => {
                let data = [];
                for (let ch = 0; ch < _data.signal.length; ch++) {
                    const N = _data.signal[ch].length;
                    data[ch] = new Array(N);
                    for (let i = 0; i < N; i++) {
                        data[ch][i] = _data.signal[ch][i];
                    }
                }
                setState("idle");
                setSignalData(data);
            })
            .catch(error => {
                setState("error");
                console.error(error);
            });
    };

    const prepareCursorsToInitialLocation = async () => {
        const { Q } = props.data;

        if (Q === null) {
            return;
        }

        const ecgComplexesPosition = parseComplexesPosition();
        computedComplexes.current = ecgComplexesPosition;
        await graphAdapter.current.moveCursors(ecgComplexesPosition);
    };

    const parseComplexesPosition = () => {
        let { Q, S, P, PDur, QTmax } = props.data;
        Q = convertMsToPoints(Q) - signalStart.current;
        PDur = convertMsToPoints(PDur);

        if (S !== null) {
            S = convertMsToPoints(S) + Q;
        }

        if (P !== null) {
            P = convertMsToPoints(P) + Q;
        } else {
            P = graphWidth.current / 4;
        }

        if (PDur === null) {
            PDur = convertMsToPoints(50);
        }

        if (QTmax !== null) {
            QTmax = convertMsToPoints(QTmax) + Q;
        } else {
            QTmax = graphWidth.current;
        }

        return { Q, S, P, PDur, QTmax };
    };

    const convertMsToPoints = timeStamp => {
        if (timeStamp !== null && !isNaN(timeStamp)) {
            return Math.round((timeStamp * fd) / 1000);
        }
    };

    const convertPointsToMs = point => {
        if (point !== null && !isNaN(point)) {
            return Math.round((point * 1000) / fd);
        }
        return null;
    };

    const onCursorMove = target => {
        const { onChanged } = props;
        const { Q, P, QTmax } = freshDataProps.current;

        const curQ = convertMsToPoints(Q) - signalStart.current;

        if ("Q" in target) {
            const curP = P === null ? computedComplexes.current.P : convertMsToPoints(P);
            const curQTmax = QTmax === null ? computedComplexes.current.QTmax : convertMsToPoints(QTmax);
            onChanged({
                P: P === null ? null : convertPointsToMs(curP + curQ - target.Q),
                QTmax: QTmax === null ? null : convertPointsToMs(curQTmax + curQ - target.Q),
                Q: convertPointsToMs(target.Q + signalStart.current),
                S: convertPointsToMs(target.S)
            });

            computedComplexes.current = {
                P: convertPointsToMs(curP),
                QTmax: convertPointsToMs(curQTmax)
            };
        } else if ("P" in target) {
            onChanged({
                P: convertPointsToMs(target.P - curQ),
                PDur: convertPointsToMs(target.PDur)
            });
        } else if ("QTmax" in target) {
            onChanged({
                QTmax: convertPointsToMs(target.QTmax - curQ)
            });
        }
    };

    return (
        <>
            <div className="canvas-wrap" style={graphWrapStyle} ref={canvasWrapRef} />
            {state === "error" ? (
                <ErrorIndicator withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} />
            ) : state === "loading" ? (
                <Spinner small transparent />
            ) : null}
        </>
    );
};

export default prepareSelectorHOC(IntervalSelectorGraph);
